'use client';

/**
 * AccentCard Component
 *
 * A card with a colored accent border on the left or top edge.
 * Inspired by modern dashboard designs with priority indicators.
 */

import Box, { type BoxProps } from '@mui/material/Box';
import { alpha, styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { type HTMLMotionProps, motion } from 'framer-motion';
import type React from 'react';
import { forwardRef } from 'react';
import { baseColors, getNeonStyles, type NeonAccent } from '../../theme/palette';
import { createNeonGlow, glassEffects } from '../../theme/shadows';
import { motionPresets, transitionPresets } from '../../theme/transitions';

// ============================================================================
// Types
// ============================================================================

export type AccentColor = 'success' | 'warning' | 'error' | 'primary' | 'secondary' | 'info';
export type AccentPosition = 'left' | 'top';
export type AccentCardVariant = 'elevated' | 'outlined' | 'flat' | 'nested';

export interface AccentCardProps extends Omit<BoxProps, 'component' | 'title'> {
  /**
   * Color of the accent border
   * @default 'primary'
   */
  accentColor?: AccentColor;

  /**
   * Position of the accent border
   * @default 'left'
   */
  accentPosition?: AccentPosition;

  /**
   * Width/height of the accent border in pixels
   * @default 4
   */
  accentSize?: number;

  /**
   * Card title (optional header)
   */
  title?: React.ReactNode;

  /**
   * Card subtitle (optional, below title)
   */
  subtitle?: React.ReactNode;

  /**
   * Content to display on the right side of header (e.g., percentage, time)
   */
  rightContent?: React.ReactNode;

  /**
   * Card variant
   * @default 'elevated'
   */
  variant?: AccentCardVariant;

  /**
   * Enable hover glow effect
   * @default false
   */
  glowOnHover?: boolean;

  /**
   * Enable hover lift animation
   * @default false
   */
  hoverLift?: boolean;

  /**
   * Enable entrance animation
   * @default false
   */
  animate?: boolean;

  /**
   * Animation delay in seconds
   * @default 0
   */
  animationDelay?: number;

  /**
   * Enable neon glow mode. Uses a matching NeonAccent color instead of solid accent border.
   * @default undefined (uses solid accent border)
   */
  neonAccent?: NeonAccent;

  /**
   * Custom border radius
   * @default 12
   */
  borderRadius?: number | string;

  /**
   * Custom padding
   * @default 3 (theme spacing)
   */
  padding?: number | string;

  /**
   * Click handler
   */
  onClick?: () => void;

  /**
   * Children content
   */
  children?: React.ReactNode;
}

// ============================================================================
// Color Mapping
// ============================================================================

const ACCENT_COLOR_MAP: Record<AccentColor, readonly [string, string]> = {
  success: [baseColors.success[500], baseColors.success[400]],
  warning: [baseColors.warning[500], baseColors.warning[400]],
  error: [baseColors.error[500], baseColors.error[400]],
  primary: [baseColors.primary[500], baseColors.primary[400]],
  secondary: [baseColors.secondary[500], baseColors.secondary[400]],
  info: [baseColors.info[500], baseColors.info[400]],
};

const getAccentColorValue = (color: AccentColor, isDark: boolean): string => {
  const [light, dark] = ACCENT_COLOR_MAP[color];
  return isDark ? dark : light;
};

const getGlowColor = (color: AccentColor): string => ACCENT_COLOR_MAP[color][0];

// ============================================================================
// Styled Components
// ============================================================================

interface StyledCardProps {
  $isDark: boolean;
  $variant: AccentCardVariant;
  $accentColor: AccentColor;
  $accentPosition: AccentPosition;
  $accentSize: number;
  $glowOnHover: boolean;
  $hoverLift: boolean;
  $borderRadius: number | string;
  $padding: number | string;
  $clickable: boolean;
  $neonAccent?: NeonAccent;
}

const StyledCard = styled(motion.div, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledCardProps>(
  ({
    theme,
    $isDark,
    $variant,
    $accentColor,
    $accentPosition,
    $accentSize,
    $glowOnHover,
    $hoverLift,
    $borderRadius,
    $padding,
    $clickable,
    $neonAccent,
  }) => {
    const accentColorValue = getAccentColorValue($accentColor, $isDark);
    const glowColorValue = getGlowColor($accentColor);
    const glass = $isDark ? glassEffects.dark : glassEffects.light;
    const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;

    const paddingValue = typeof $padding === 'number' ? theme.spacing($padding) : $padding;

    const borderRadiusValue = typeof $borderRadius === 'number' ? `${$borderRadius}px` : $borderRadius;

    // Base styles
    const baseStyles = {
      position: 'relative' as const,
      borderRadius: borderRadiusValue,
      padding: paddingValue,
      overflow: 'hidden',
      transition: transitionPresets.card,
      cursor: $clickable ? 'pointer' : 'default',
    };

    // Accent border styles
    const accentStyles =
      $accentPosition === 'left'
        ? {
            borderLeft: `${$accentSize}px solid ${accentColorValue}`,
            borderTopLeftRadius: borderRadiusValue,
            borderBottomLeftRadius: borderRadiusValue,
          }
        : {
            borderTop: `${$accentSize}px solid ${accentColorValue}`,
            borderTopLeftRadius: borderRadiusValue,
            borderTopRightRadius: borderRadiusValue,
          };

    // Variant-specific styles
    const variantStyles = {
      elevated: {
        background: $isDark
          ? 'rgba(20, 20, 20, 0.95)' // Near-black for midnight theme
          : glass.background,
        backdropFilter: glass.backdropBlur,
        border: `1px solid ${$isDark ? 'rgba(255, 255, 255, 0.08)' : glass.border}`,
        boxShadow: glass.shadow,
      },
      outlined: {
        background: 'transparent',
        border: `1px solid ${$isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'}`,
        boxShadow: 'none',
      },
      flat: {
        background: $isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
        border: 'none',
        boxShadow: 'none',
      },
      nested: {
        background: $isDark
          ? 'rgba(26, 26, 26, 0.8)' // Slightly lighter than parent
          : 'rgba(255, 255, 255, 0.5)',
        border: `1px solid ${$isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.06)'}`,
        boxShadow: 'none',
      },
    };

    // Hover styles
    const hoverStyles = {
      '&:hover': {
        ...(($variant === 'elevated' || $variant === 'nested') && {
          background: $isDark ? 'rgba(30, 30, 30, 0.95)' : glass.backgroundHover,
          border: `1px solid ${$isDark ? 'rgba(255, 255, 255, 0.12)' : glass.borderHover}`,
          boxShadow: $glowOnHover ? `${glass.shadowHover}, 0 0 20px ${alpha(glowColorValue, 0.2)}` : glass.shadowHover,
        }),
        ...($hoverLift && {
          transform: 'translateY(-4px)',
        }),
      },
      '&:active': {
        ...($clickable && {
          transform: 'translateY(-2px)',
        }),
      },
    };

    // If neonAccent is set, use neon glow styling instead
    if (neonStyles && $neonAccent) {
      return {
        position: 'relative' as const,
        borderRadius: borderRadiusValue,
        padding: paddingValue,
        overflow: 'hidden',
        transition: transitionPresets.card,
        cursor: $clickable ? 'pointer' : 'default',

        // Neon glow styles
        background: neonStyles.background,
        border: `1px solid ${neonStyles.border}`,
        boxShadow: $glowOnHover ? 'none' : createNeonGlow($neonAccent, 'subtle'),

        // Hover effects
        '&:hover': {
          boxShadow: $glowOnHover ? createNeonGlow($neonAccent, 'medium') : undefined,
          ...($hoverLift && {
            transform: 'translateY(-4px)',
          }),
        },

        // Active state
        '&:active': {
          ...($clickable && {
            transform: 'translateY(-2px)',
          }),
        },
      };
    }

    return {
      ...baseStyles,
      ...accentStyles,
      ...variantStyles[$variant],
      ...hoverStyles,
      // Override border on the accent side
      ...($accentPosition === 'left' && {
        borderLeftWidth: $accentSize,
        borderLeftStyle: 'solid',
        borderLeftColor: accentColorValue,
      }),
      ...($accentPosition === 'top' && {
        borderTopWidth: $accentSize,
        borderTopStyle: 'solid',
        borderTopColor: accentColorValue,
      }),
    };
  },
);

const CardHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: theme.spacing(2),
  gap: theme.spacing(2),
}));

const HeaderContent = styled(Box)({
  flex: 1,
  minWidth: 0,
});

// ============================================================================
// Component
// ============================================================================

export const AccentCard = forwardRef<HTMLDivElement, AccentCardProps>(
  (
    {
      children,
      accentColor = 'primary',
      accentPosition = 'left',
      accentSize = 4,
      title,
      subtitle,
      rightContent,
      variant = 'elevated',
      glowOnHover = false,
      hoverLift = false,
      animate = false,
      animationDelay = 0,
      neonAccent,
      borderRadius = 12,
      padding = 3,
      onClick,
      sx,
      ...props
    },
    ref,
  ) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    // Animation variants
    const animationProps: HTMLMotionProps<'div'> = animate
      ? {
          initial: motionPresets.fadeInUp.initial,
          animate: motionPresets.fadeInUp.animate,
          transition: {
            ...motionPresets.fadeInUp.transition,
            delay: animationDelay,
          },
        }
      : {};

    const hasHeader = title || subtitle || rightContent;

    return (
      <Box sx={sx} {...props}>
        <StyledCard
          ref={ref}
          $isDark={isDark}
          $variant={variant}
          $accentColor={accentColor}
          $accentPosition={accentPosition}
          $accentSize={accentSize}
          $glowOnHover={glowOnHover}
          $hoverLift={hoverLift}
          $borderRadius={borderRadius}
          $padding={padding}
          $clickable={!!onClick}
          $neonAccent={neonAccent}
          onClick={onClick}
          {...animationProps}
        >
          {hasHeader && (
            <CardHeader>
              <HeaderContent>
                {title &&
                  (typeof title === 'string' ? (
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: 'text.primary',
                        lineHeight: 1.3,
                      }}
                    >
                      {title}
                    </Typography>
                  ) : (
                    title
                  ))}
                {subtitle &&
                  (typeof subtitle === 'string' ? (
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                        mt: 0.5,
                      }}
                    >
                      {subtitle}
                    </Typography>
                  ) : (
                    subtitle
                  ))}
              </HeaderContent>
              {rightContent && <Box sx={{ flexShrink: 0 }}>{rightContent}</Box>}
            </CardHeader>
          )}
          {children}
        </StyledCard>
      </Box>
    );
  },
);

AccentCard.displayName = 'AccentCard';

export default AccentCard;
